import React, { useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import "../../../assets/css/custom.css";
import { THEME_COLOR3, UNGROUPED } from "../../../constants";

// icons
import { ReactComponent as PlusIcon } from "../../../assets/images/add_plus.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete1.svg";

import { featureServices } from "../../../api/useQueryHooks";
import { ModalModes } from "./constants"
import { QUERY_KEYS } from "../../../api/QueryKeys";

import Container from "../../../components/Container";
import Loader from "../../../components/Loader";
import AlertMsg from "../../../components/AlertMsg";
import AppDialog from "../../../components/AppDialog";
import RestaurantHeader from "../../../components/RestaurantHeader";

import GroupModalDialog from "./GroupModalDialog";
import FeatureGroupCard from "./FeatureGroup";
import FeatureModalDialog from "./FeatureModalDialog";

const FeaturesList = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [groupModalState, setGroupModalState] = useState({ open: false });
    const [featureModalState, setFeatureModalState] = useState({ open: false });
    const [msgAlert, setMsgAlert] = useState({ open: false });
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [appDialogState, setAppDialogState] = useState({ open: false });
    const [isLoading, setLoading] = useState(false);
    const [takenGroupName, setTakenGroupName] = useState("");
    const [takenFeatureName, setTakenFeatureName] = useState("");
    const [takenFeatureId, setTakenFeatureId] = useState("");
    const [collapseGroups, setCollapseGroups] = useState(false);

    // Feature Groups Services
    const { data: featureGroupList, isLoading: isLoadingFeatureGroupList } = featureServices.useGetAllGroups();
    const createGroupMutation = featureServices.useCreateGroup((newGroupData) => {
        queryClient.setQueryData([QUERY_KEYS.FeatureGroups], (oldData) => ({ ...oldData, groups: [...oldData.groups, newGroupData] }));
        setMsgAlert({ open: true, message: "Feature group created successfully", msgType: "success" });
        setGroupModalState({ open: false });
    }, () => {
        setMsgAlert({ open: true, message: "Feature group creation failed", msgType: "error" });
        setGroupModalState({ open: false });
    });
    const updateGroupMutation = featureServices.useUpdateGroup((updatedGroupData) => {
        queryClient.setQueryData([QUERY_KEYS.FeatureGroups], (oldData) => ({
            ...oldData,
            groups: oldData.groups.map((group) => group._id === updatedGroupData._id ? updatedGroupData : group)
        }));

        setMsgAlert({ open: true, message: "Feature group updated successfully", msgType: "success" });
        setGroupModalState({ open: false });
    }, () => {
        setMsgAlert({ open: true, message: "Feature group update failed", msgType: "error" });
        setGroupModalState({ open: false });
    });

    const deleteGroupMutation = featureServices.useDeleteGroup((deletedGroupId) => {
        queryClient.setQueryData([QUERY_KEYS.FeatureGroups], (oldData) =>
            ({ ...oldData, groups: oldData.groups.filter((group) => group._id !== deletedGroupId) }));
        setMsgAlert({ open: true, message: "Feature group deleted successfully", msgType: "success" });
        closeAppDialog();
        setLoading(false);
    }, () => {
        setMsgAlert({ open: true, message: "Feature group deletion failed", msgType: "error" });
        closeAppDialog();
        setLoading(false);
    })

    // Feature Services
    const createFeatureMutation = featureServices.useCreateFeature((newFeatureData) => {
        queryClient.setQueryData([QUERY_KEYS.FeatureGroups], (oldData) => {
            if (!oldData) return oldData;
            const { _idGroup } = newFeatureData;

            // If the new feature belongs to a group
            if (_idGroup) {
                return {
                    ...oldData,
                    groups: oldData.groups.map((group) => group._id === _idGroup ?
                        { ...group, features: [...group.features, newFeatureData] } : group
                    )
                };
            }
            // If the feature is ungrouped
            return {
                ...oldData,
                ungrouped: [...oldData.ungrouped, newFeatureData]
            };
        });

        setMsgAlert({ open: true, message: "Feature created successfully", msgType: "success" });
        resetFeatureModal();
    },
        () => {
            setMsgAlert({ open: true, message: "Feature creation failed", msgType: "error" });
        }
    );

    const updateFeatureMutation = featureServices.useUpdateFeature((updatedFeatureData) => {
        queryClient.setQueryData([QUERY_KEYS.FeatureGroups], (oldData) => {
            if (!oldData) return oldData;

            const updatedGroups = oldData.groups.map((group) => {
                const updatedFeatures = group.features.map((feature) =>
                    feature._id === updatedFeatureData._id ? updatedFeatureData : feature
                );
                return { ...group, features: updatedFeatures };
            });

            const updatedUngrouped = oldData.ungrouped.map((feature) =>
                feature._id === updatedFeatureData._id ? updatedFeatureData : feature
            );

            return {
                ...oldData,
                groups: updatedGroups,
                ungrouped: updatedUngrouped,
            };
        });

        setMsgAlert({ open: true, message: "Feature updated successfully", msgType: "success" });
        resetFeatureModal();
    }, () => {
        setMsgAlert({ open: true, message: "Feature update failed", msgType: "error" });
    });


    const deleteFeatureMutation = featureServices.useDeleteFeature(({ featureData, groupId }) => {
        queryClient.setQueryData([QUERY_KEYS.FeatureGroups], (oldData) => {
            if (!oldData) return oldData;

            if (groupId) {
                return {
                    ...oldData,
                    groups: oldData.groups.map(group => group._id === groupId ?
                        { ...group, features: group.features.filter(f => f._id !== featureData._id) }
                        : group
                    )
                };
            } else {
                return {
                    ...oldData,
                    ungrouped: oldData.ungrouped.filter(f => f._id !== featureData._id)
                };
            }
        });
        closeAppDialog();
        setMsgAlert({ open: true, message: "Feature deleted successfully", msgType: "success" });
        setLoading(false);
    }, () => {
        closeAppDialog();
        setMsgAlert({ open: true, message: "Feature deletion failed", msgType: "error" });
        setLoading(false);
    });

    const updateGroupAfterFeatureDrop = (modifiedGroups) => {
        setTimeout(() => {
            for (const group of modifiedGroups) {
                updateGroupMutation.mutate(group);
            }
        }, 600);
    }

    const moveFeatureCard = useCallback(
        // destUngrouped is there for micro optimization. Better have a boolean check than have a string comparison
        (featureId, featureIndex, newFeatureIndex, sourceGroupId, sourceGroupIndex, destGroupId, destGroupIndex, destUngrouped) => {

            queryClient.setQueryData([QUERY_KEYS.FeatureGroups], (prevData) => {
                if (!prevData) return prevData;

                const { groups, ungrouped } = prevData;

                // Clone groups and ungrouped to avoid mutation
                const newGroups = groups.map(group => ({ ...group, features: [...group.features] }));
                const newUngrouped = [...ungrouped];

                let movedFeature = null;

                // 1 Remove from source
                if (sourceGroupId === UNGROUPED) movedFeature = newUngrouped.splice(featureIndex, 1);
                else movedFeature = newGroups[sourceGroupIndex].features.splice(featureIndex, 1);

                // 2️ Insert into destination
                if (destUngrouped) newUngrouped.splice(newFeatureIndex, 0, movedFeature[0]);
                else newGroups[destGroupIndex].features.splice(newFeatureIndex, 0, movedFeature[0]);

                // 3 Send the data to backend to persist
                const updatedFeatureData = { ...movedFeature[0] }
                if (!destUngrouped) updatedFeatureData._idGroup = destGroupId;

                const updatedGroups = [];
                if (sourceGroupId !== UNGROUPED) updatedGroups.push(newGroups[sourceGroupIndex]);
                if (!destUngrouped) updatedGroups.push(newGroups[destGroupIndex]);

                updateGroupAfterFeatureDrop(updatedGroups);

                // 4 Return updated raw cache
                return {
                    groups: newGroups,
                    ungrouped: newUngrouped
                };
            });
        },
        []
    );

    const updateGroupAfterDrop = () => {
        for (let idx = 0; idx < featureGroupList.length; idx++) {
            if (featureGroupList[idx].groupOrder !== idx && featureGroupList[idx]._id !== UNGROUPED) {
                console.warn("sending update group request for: ", featureGroupList[idx].groupName, featureGroupList[idx]);
                featureGroupList[idx].groupOrder = idx;
                updateGroupMutation.mutate(featureGroupList[idx]);
            }
        }
    }

    const moveGroup = useCallback((dragIndex, hoverIndex) => {
        queryClient.setQueryData([QUERY_KEYS.FeatureGroups], (prevData) => {
            if (!prevData) return prevData;

            const { groups, ungrouped } = prevData;

            const newGroups = [...groups];
            const [draggedGroup] = newGroups.splice(dragIndex, 1);
            newGroups.splice(hoverIndex, 0, draggedGroup);

            return {
                groups: newGroups,
                ungrouped: [...ungrouped]
            };
        });
    }, []);


    const handleAddGroup = () => {
        setSelectedGroup({
            groupName: "",
            groupOrder: featureGroupList.length - 1
        });
        setGroupModalState({ open: true, mode: ModalModes.ADD });
    }

    const handleEditGroup = (groupData) => {
        setSelectedGroup(groupData);
        setGroupModalState({ open: true, mode: ModalModes.EDIT });
    }

    const handleDeleteGroup = (groupData) => {
        setAppDialogState({
            open: true,
            title: <Box>
                <Typography sx={{ margin: 0 }}>
                    < DeleteIcon width={"2.5rem"} height={"2.5rem"} />
                </Typography>
                <Typography variant="h2" sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}>
                    Delete Group?
                </Typography>
            </Box>,
            content: <Box sx={{ fontSize: "1rem", fontFamily: "GilroySemiBold", margin: "1rem 0" }}>
                Are you sure you want to delete {groupData.groupName}?
            </Box>,
            firstBtnText: "Cancel",
            secondBtnText: "Delete",
            onClose: closeAppDialog,
            onConfirm: () => deleteGroups(groupData),
        });
    }

    const deleteGroups = (groupData) => {
        setLoading(true);
        deleteGroupMutation.mutate(groupData._id);
        for (let i = groupData.groupOrder + 1; i < featureGroupList.groups.length; i++) {
            const updatedGroup = { ...featureGroupList.groups[i], groupOrder: i - 1 };
            updateGroupMutation.mutate(updatedGroup);
        }
    }

    const handleFeatureDelete = (feature, groupId) => {
        setAppDialogState({
            open: true,
            title: <Box>
                <Typography sx={{ margin: 0 }}>
                    < DeleteIcon width={"2.5rem"} height={"2.5rem"} />
                </Typography>
                <Typography variant="h2" sx={{ fontSize: "1.5rem", fontFamily: "GilroySemiBold" }}>
                    Delete Feature?
                </Typography>
            </Box>,
            content: <Box>Are you sure you want to delete {feature.label}</Box>,
            firstBtnText: "Cancel",
            secondBtnText: "Delete",
            onClose: closeAppDialog,
            onConfirm: () => {
                setLoading(true);
                deleteFeatureMutation.mutate({
                    featureData: feature,
                    groupId: groupId === UNGROUPED ? null : groupId,
                });
            },
        });
    }

    const handleFeatureAdd = (groupId) => {
        setFeatureModalState({ open: true, mode: ModalModes.ADD });
        const newFeature = {
            label: "",
            description: "",
            uid: "",
        }
        if (groupId !== UNGROUPED) newFeature._idGroup = groupId;
        setSelectedFeature(newFeature);
    }

    const handleFeatureEdit = (feature, groupId, index) => {
        setFeatureModalState({ open: true, mode: ModalModes.EDIT, feature, groupId, index });
        setSelectedFeature(feature);
    }

    const handleFeatureModalClose = () => {
        resetFeatureModal();
    }

    const resetFeatureModal = () => {
        setFeatureModalState({ open: false });
        setTakenFeatureId("");
        setTakenFeatureName("");
        setSelectedFeature(null);
    }

    const handleGroupSave = updatedGroup => {
        if (isGroupNameTaken(updatedGroup.groupName)) {
            setTakenGroupName(updatedGroup.groupName);
            return;
        } else setTakenGroupName("");

        if (groupModalState.mode === ModalModes.ADD) {
            createGroupMutation.mutate(updatedGroup);
        } else {
            updateGroupMutation.mutate(updatedGroup);
        }
    }

    const isGroupNameTaken = groupName => {
        const name = groupName.trim().toLowerCase();
        const nameIndex = featureGroupList.findIndex(group => group.groupName.toLowerCase() === name);
        return nameIndex >= 0 ? true : false;
    }

    const handleFeatureSave = (newFeature) => {
        if (isFeatureNameTaken(newFeature)) {
            setTakenFeatureName(newFeature.label);
            return;
        } else setTakenFeatureName("");

        if (isFeatureIdTaken(newFeature)) {
            setTakenFeatureId(newFeature.uid);
            return;
        } else setTakenFeatureId("");


        if (featureModalState.mode === ModalModes.ADD) createFeatureMutation.mutate(newFeature);
        else updateFeatureMutation.mutate(newFeature);
    }

    const isFeatureNameTaken = feature => {
        const name = feature.label.trim().toLowerCase();
        for (const group of featureGroupList) {
            const nameIndex = group.features.findIndex(feat => (feat.label.toLowerCase() === name && feat._id !== feature._id));
            if (nameIndex >= 0) return true;
        }
        return false;
    }

    const isFeatureIdTaken = feature => {
        const id = feature.uid.trim().toLowerCase();
        for (const group of featureGroupList) {
            const idIndex = group.features.findIndex(feat => feat.uid?.toLowerCase() === id && feat._id !== feature._id);
            if (idIndex >= 0) return true;
        }
        return false;
    }

    const closeAppDialog = () => setAppDialogState({ open: false });
    const handleCollapseGroup = () => setCollapseGroups(!collapseGroups);


    return (
        <DndProvider backend={HTML5Backend}>
            <Container
                page={"global_settings"}
                searchVal={""}
                onSearch={() => null}
                searchLabel={null}
            >
                {isLoadingFeatureGroupList ? (
                    <Loader height={{ height: window.innerHeight - 150 }} />
                ) : (
                    <>
                        <Box sx={styles.titleMainBox}>
                            <RestaurantHeader
                                title={"Features"}
                                onBackBtnClick={() => navigate("/global_settings")}
                                showTitleOnly={true}
                            />
                            <Button sx={styles.createButton} onClick={handleAddGroup}>
                                <PlusIcon />
                                Add Group
                            </Button>
                        </Box>
                        {/* features group cards  */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: "100%", }}>
                            {featureGroupList.length > 0 ? featureGroupList.map((list, index) => (
                                <FeatureGroupCard
                                    key={list._id}
                                    groupData={list}
                                    index={index}
                                    moveGroup={moveGroup}
                                    moveFeatureCard={moveFeatureCard}
                                    handleEditGroup={handleEditGroup}
                                    handleDeleteGroup={handleDeleteGroup}
                                    handleFeatureAdd={handleFeatureAdd}
                                    handleFeatureEdit={handleFeatureEdit}
                                    handleFeatureDelete={handleFeatureDelete}
                                    collapseGroups={collapseGroups}
                                    handleCollapseGroup={handleCollapseGroup}
                                    updateGroupAfterDrop={updateGroupAfterDrop}
                                />
                            )) : (<Card sx={styles.mainGrid}>
                                <Grid container direction="column" justifyContent="center" alignItems={"center"}>
                                    <Typography variant={"h6"} sx={styles.userText}>
                                        No Group Exists
                                    </Typography>
                                    <Box>
                                        <Button sx={[styles.createButton, { marginTop: '1rem' }]} onClick={handleAddGroup}>
                                            Add A Group
                                        </Button>
                                    </Box>
                                </Grid>
                            </Card>)
                            }
                        </Box>
                    </>
                )}

                <AlertMsg
                    msgAlert={msgAlert}
                    onCloseAlertMsg={() => setMsgAlert({ open: false })}
                />

                <AppDialog
                    open={appDialogState.open}
                    onClose={appDialogState.onClose}
                    content={appDialogState.content}
                    title={appDialogState.title}
                    firstBtnContent={appDialogState.firstBtnText}
                    secondBtnContent={isLoading ? "Please wait..." : appDialogState.secondBtnText}
                    firstBtnClickHandler={appDialogState.onClose}
                    secondBtnClickHandler={appDialogState.onConfirm}
                    options={{ btnLoading: isLoading }}
                />

                {groupModalState.open &&
                    <GroupModalDialog
                        open={groupModalState.open}
                        onClose={() => setGroupModalState({ ...groupModalState, open: false })}
                        onSave={handleGroupSave}
                        groupData={selectedGroup}
                        mode={groupModalState.mode}
                        loading={createGroupMutation.isPending || updateGroupMutation.isPending}
                        takenGroupName={takenGroupName}
                    />}
                {
                    featureModalState.open &&
                    <FeatureModalDialog
                        open={featureModalState.open}
                        onClose={handleFeatureModalClose}
                        onSave={handleFeatureSave}
                        featureData={selectedFeature}
                        groupData={selectedGroup}
                        mode={featureModalState.mode}
                        loading={createFeatureMutation.isPending || updateFeatureMutation.isPending}
                        takenFeatureName={takenFeatureName}
                        takenFeatureId={takenFeatureId}
                    />
                }
            </Container>
        </DndProvider >
    );
};

export default FeaturesList;

const styles = {
    titleMainBox: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 2,
    },
    userText: {
        fontFamily: "GilroyBold",
    },
    mainGrid: {
        cursor: "pointer",
        borderRadius: 3,
        width: "100%",
        marginBottom: 2,
        padding: 1.3,
    },
    labelTitle: {
        fontFamily: "GilroyBold",
        fontSize: 14,
    },
    selectList: {
        "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            fontFamily: "GilroySemiBold",
            fontSize: 13,
            paddingTop: 0.8,
            paddingBottom: 0.8,
        },
    },
    selectListItem: {
        fontFamily: "GilroySemiBold",
        color: THEME_COLOR3,
        fontSize: 13,
    },
    createButton: {
        backgroundColor: "#000",
        color: "#FFF",
        fontFamily: "GilroyBold",
        fontSize: 14,
        borderRadius: 1,
        "&:hover": {
            backgroundColor: "#000 !important",
        },
    },
};
